import { useState } from 'react';
import { RefreshCw, Crown, Trophy, ChevronRight, ChevronDown } from 'lucide-react';

// ============================================================
// Road to WSOP — live leaderboard (moved from Reports)
// ============================================================
export function WsopLeaderboardReport({ data, loading }) {
  const QUALIFY_COUNT = 9;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={28} className="animate-spin text-amber-500" />
      </div>
    );
  }
  if (!data) return null;

  const { leaderboard = [], promoName, promoStart, promoEnd, promoStatus, totalPlayers, lastUpdated } = data;

  const statusColors = {
    active: 'bg-green-500/20 text-green-400 border border-green-500/30',
    upcoming: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    ended: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
  };
  const rankStyle = (rank) => {
    if (rank === 1) return 'text-yellow-400 font-bold text-lg';
    if (rank === 2) return 'text-gray-300 font-bold';
    if (rank === 3) return 'text-amber-600 font-bold';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy size={22} className="text-amber-500" />
              {promoName || 'Road to WSOP'}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {promoStart} &rarr; {promoEnd}
              {lastUpdated && (
                <span className="ml-3 text-gray-500">
                  Updated {new Date(lastUpdated).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} PT
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-3 py-1 rounded-full font-medium capitalize ${statusColors[promoStatus] || statusColors.active}`}>{promoStatus}</span>
            <span className="text-xs px-3 py-1 rounded-full bg-gray-700 text-gray-300">{totalPlayers} players ranked</span>
          </div>
        </div>
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
          Top <strong>{QUALIFY_COUNT} players</strong> qualify. XP uses the Pacific time-of-day multipliers.
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase tracking-wide">
              <th className="text-left px-6 py-3 w-16">Rank</th>
              <th className="text-left px-6 py-3">Player</th>
              <th className="text-left px-4 py-3">Member #</th>
              <th className="text-right px-6 py-3">XP</th>
              <th className="text-right px-4 py-3">Sessions</th>
              <th className="text-right px-6 py-3">Hours</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((player) => {
              const qualifies = player.rank <= QUALIFY_COUNT;
              return (
                <tr key={player.memberNumber} className={`border-b border-gray-700/50 ${qualifies ? 'hover:bg-amber-500/5' : 'hover:bg-gray-700/30 opacity-70'} ${player.rank === QUALIFY_COUNT ? 'border-b-2 border-amber-500/40' : ''}`}>
                  <td className="px-6 py-4"><span className={rankStyle(player.rank)}>{player.rank}</span></td>
                  <td className="px-6 py-4"><span className={`font-medium ${qualifies ? 'text-white' : 'text-gray-400'}`}>{player.fullName}</span></td>
                  <td className="px-4 py-4 text-gray-500 font-mono text-xs">{player.memberNumber}</td>
                  <td className="px-6 py-4 text-right"><span className={`font-bold ${qualifies ? 'text-amber-400' : 'text-gray-500'}`}>{(player.xp || 0).toLocaleString()}</span></td>
                  <td className="px-4 py-4 text-right text-gray-400">{player.sessions}</td>
                  <td className="px-6 py-4 text-right text-gray-400">{player.hours}h</td>
                </tr>
              );
            })}
            {leaderboard.length === 0 && (
              <tr><td colSpan={6} className="text-center text-gray-500 py-12">No leaderboard data yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// WPT Seat Qualifier — Monthly Leaderboards (with points-by-time breakdown)
// ============================================================
const hrs = (min) => ((min || 0) / 60).toFixed(1);

function WindowPill({ label, mult, pts, min, color }) {
  return (
    <div className="flex flex-col rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 min-w-[150px]">
      <span className="text-[11px] uppercase tracking-wide text-gray-500">{label} <span className={color}>{mult}</span></span>
      <span className={`text-base font-bold ${color}`}>{(pts || 0).toLocaleString()} <span className="text-xs font-normal text-gray-500">pts</span></span>
      <span className="text-[11px] text-gray-500">{hrs(min)}h</span>
    </div>
  );
}

export function WptMonthlyReport({ data, loading }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={28} className="animate-spin text-amber-500" />
      </div>
    );
  }
  if (!data) return null;

  const { promoName, monthlyChips = [1500, 1000, 500], months = [] } = data;
  const rankStyle = (rank) => {
    if (rank === 1) return 'text-yellow-400 font-bold text-lg';
    if (rank === 2) return 'text-gray-300 font-bold';
    if (rank === 3) return 'text-amber-600 font-bold';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Crown size={22} className="text-amber-500" />
          {promoName || 'WPT Seat Qualifier'} &mdash; Monthly Leaderboards
        </h2>
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm">
          Each month's top 3 earn bonus chips: <strong>{(monthlyChips[0] || 0).toLocaleString()}</strong> / <strong>{(monthlyChips[1] || 0).toLocaleString()}</strong> / <strong>{(monthlyChips[2] || 0).toLocaleString()}</strong> chips.
          Points use the 3&times; (12&ndash;6 AM) / 5&times; (6 AM&ndash;6 PM) / 1&times; (6 PM&ndash;12 AM) Pacific multipliers. <span className="text-amber-400">Tap a player to see their points by time.</span>
        </div>
      </div>

      {months.length === 0 && (
        <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-500">No monthly data yet.</div>
      )}

      {months.map((m) => (
        <div key={m.month} className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">{m.label}</h3>
            <span className="text-xs px-3 py-1 rounded-full bg-gray-700 text-gray-300">{m.totalPlayers} players</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase tracking-wide">
                <th className="text-left px-6 py-3 w-16">Rank</th>
                <th className="text-left px-6 py-3">Player</th>
                <th className="text-left px-4 py-3">Member #</th>
                <th className="text-right px-6 py-3">XP</th>
                <th className="text-right px-6 py-3">Bonus Chips</th>
              </tr>
            </thead>
            <tbody>
              {m.standings.map((p) => {
                const awarded = p.bonusChips > 0;
                const key = `${m.month}:${p.memberNumber}`;
                const open = expanded.has(key);
                const b = p.breakdown;
                return (
                  <>
                    <tr
                      key={key}
                      onClick={() => b && toggle(key)}
                      className={`border-b border-gray-700/50 transition-colors cursor-pointer ${awarded ? 'hover:bg-amber-500/5' : 'hover:bg-gray-700/30'} ${p.rank === 3 ? 'border-b-2 border-amber-500/40' : ''}`}
                    >
                      <td className="px-6 py-4"><span className={rankStyle(p.rank)}>{p.rank}</span></td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-2">
                          {b && (open ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />)}
                          <span className={`font-medium ${awarded ? 'text-white' : 'text-gray-300'}`}>{p.firstName} {p.lastName}</span>
                          {p.role && p.role !== 'user' && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">staff</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-500 font-mono text-xs">{p.memberNumber}</td>
                      <td className="px-6 py-4 text-right"><span className={`font-bold ${awarded ? 'text-amber-400' : 'text-gray-400'}`}>{(p.xp || 0).toLocaleString()}</span></td>
                      <td className="px-6 py-4 text-right">
                        {awarded ? <span className="font-semibold text-green-400">+{p.bonusChips.toLocaleString()}</span> : <span className="text-gray-600">&mdash;</span>}
                      </td>
                    </tr>
                    {open && b && (
                      <tr key={`${key}-detail`} className="bg-gray-900/40 border-b border-gray-700/50">
                        <td></td>
                        <td colSpan={4} className="px-6 py-4">
                          <div className="flex flex-wrap gap-3">
                            <WindowPill label="12am&ndash;6am" mult="3&times;" pts={b.late?.points} min={b.late?.minutes} color="text-purple-300" />
                            <WindowPill label="6am&ndash;6pm" mult="5&times;" pts={b.day?.points} min={b.day?.minutes} color="text-amber-300" />
                            <WindowPill label="6pm&ndash;12am" mult="1&times;" pts={b.evening?.points} min={b.evening?.minutes} color="text-gray-300" />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {m.standings.length === 0 && (
                <tr><td colSpan={5} className="text-center text-gray-500 py-8">No players this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
